<template>
  <PrimeMultiSelect
    ref="multi-select"
    :modelValue="modelValue"
    @update:modelValue="updateSelection"
    @blur="emit('blur', $event)"
    @keydown.capture="onKeydown"
    :options="primeOptions"
    optionLabel="label"
    optionValue="value"
    :filterFields="['searchText']"
    :showToggleAll="single ? false : showToggleAll"
    :placeholder
    :disabled
    :inputId
    :filter
    autoFilterFocus
    :filterPlaceholder="filterPlaceholder ?? 'Search'"
    :panelClass="
      twMerge('klimate-multiselect-panel', appendToSelf ? 'w-full' : undefined, props.optionsClass)
    "
    :appendTo="appendToSelf ? 'self' : 'body'"
    :class="
      twMerge(
        'klimate-inline-select text-sm focus:ring-active ring-1 ring-inset ring-gray-300',
        error ? 'ring-red-300 text-red-900' : undefined,
        asNavigation ? 'select-navigation' : undefined,
        props.class
      )
    "
    :invalid="!!error"
    :pt="{ list: { 'aria-multiselectable': single ? 'false' : 'true' } }"
  >
    <template v-if="slots.value || !modelValue?.length" #value>
      <slot name="value" :value="modelValue" :placeholder="placeholder">
        {{ placeholder || '\u00a0' }}
      </slot>
    </template>
    <template #option="{ option: { item: option } }">
      <div class="group flex min-w-0 flex-1 items-center gap-2">
        <span v-if="!single" class="flex h-5 w-5 flex-none items-center justify-center">
          <CheckIcon v-if="isOptionSelected(option)" class="h-5 w-5" aria-hidden="true" />
        </span>
        <span class="min-w-0 flex-1 truncate">
          <slot name="option-label" :option="option" :label="getOptionLabel(option)">
            {{ getOptionLabel(option) }}
          </slot>
        </span>
        <span
          v-if="slots['option-actions']"
          class="invisible flex w-16 flex-none items-center justify-end group-hover:visible group-focus-within:visible"
          @mousedown.prevent.stop
          @pointerdown.prevent.stop
          @click.capture="closePanel"
          @click.stop
        >
          <slot name="option-actions" :option="option" :selected="isOptionSelected(option)" />
        </span>
      </div>
    </template>
    <template v-if="slots.footer" #footer>
      <div
        class="border-t border-gray-200 px-3 py-2"
        @mousedown.prevent.stop
        @pointerdown.prevent.stop
        @click.capture="closePanel"
        @click.stop
      >
        <slot name="footer" />
      </div>
    </template>
  </PrimeMultiSelect>
</template>

<style>
.klimate-inline-select {
  @apply w-full justify-between rounded-sm border-0 bg-white py-1.5 px-3 shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:ring-gray-200;
}
.klimate-multiselect-panel {
  @apply overflow-hidden;
}
.klimate-inline-select .p-multiselect-label {
  @apply p-0 truncate;
}
.klimate-inline-select.has-clear .p-multiselect-label {
  @apply pr-8;
}
.klimate-multiselect-panel .p-multiselect-items-wrapper {
  @apply max-w-full overflow-x-hidden;
  border-radius: 0;
  box-shadow: none;
}
.klimate-multiselect-panel .p-multiselect-items {
  @apply min-w-0 max-w-full py-1;
}
.klimate-multiselect-panel .p-multiselect-item .p-checkbox {
  @apply hidden;
}
.klimate-multiselect-panel .p-multiselect-item {
  @apply flex min-w-0 w-full overflow-hidden text-sm py-1.5 gap-2;
}
.klimate-multiselect-panel .p-multiselect-item:hover {
  @apply text-white bg-active-500;
}
.klimate-multiselect-panel .p-multiselect-item.p-focus {
  @apply text-white bg-active-500;
}
.klimate-multiselect-panel .p-multiselect-item[aria-selected='true'] {
  @apply text-active-500 bg-white;
}
.klimate-multiselect-panel .p-multiselect-item[aria-selected='true']:hover {
  @apply text-white bg-active-500;
}
.klimate-multiselect-panel .p-multiselect-filter-container {
  @apply w-full;
}
.klimate-multiselect-panel .p-multiselect-header .p-checkbox {
  @apply h-4 w-4 mr-2;
}
.klimate-multiselect-panel .p-multiselect-header .p-checkbox-box {
  @apply hidden;
}
.klimate-multiselect-panel .p-multiselect-header .p-multiselect-close {
  @apply hidden;
}
.klimate-multiselect-panel .p-multiselect-header .p-checkbox-input {
  @apply h-4 w-4 border-gray-300 border text-indigo-600 focus:ring-indigo-600 disabled:bg-gray-50 disabled:border-gray-100 rounded;
}
.klimate-multiselect-panel .p-multiselect-filter {
  @apply text-gray-900 ring-gray-300 placeholder:text-gray-400;
  @apply block w-full rounded-sm border-0 py-1.5 shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:ring-gray-200 sm:text-sm sm:leading-6;
}
.klimate-multiselect-panel .p-multiselect-filter-icon {
  @apply !hidden;
}
.klimate-inline-select.p-disabled {
  @apply cursor-not-allowed bg-gray-50 text-gray-500 ring-gray-200;
}
.klimate-inline-select.select-navigation {
  @apply bg-[#2b2b2b] text-white ring-0 px-0;
}
.klimate-inline-select.select-navigation .p-multiselect-trigger {
  @apply text-white;
}
</style>

<script setup lang="ts" generic="TOption extends Object, TValue = unknown">
import { CheckIcon } from '@heroicons/vue/20/solid'
import PrimeMultiSelect from 'primevue/multiselect'
import { twMerge } from 'tailwind-merge'
import { computed, useTemplateRef } from 'vue'

const modelValue = defineModel<TValue[]>()

const props = defineProps<{
  inputId?: string
  single?: boolean
  error?: string | null
  asNavigation?: boolean
  options: TOption[]
  disabled?: boolean
  optionSearchText?: (option: TOption) => string
  optionLabel?: string | ((t: TOption) => string)
  optionValue?: string | ((t: TOption) => TValue)
  placeholder?: string
  optionsClass?: string
  appendToSelf?: boolean
  showToggleAll?: boolean
  filter?: boolean
  filterPlaceholder?: string
  class?: string
}>()
const emit = defineEmits<{ blur: [event: Event] }>()
const slots = defineSlots<{
  value?: (props: { value: TValue[] | undefined; placeholder?: string }) => unknown
  'option-label'?: (props: { option: TOption; label: string }) => unknown
  'option-actions'?: (props: { option: TOption; selected: boolean }) => unknown
  footer?: () => unknown
}>()

function updateSelection(values: TValue[] | null) {
  // Re-selecting the current option (including via Tab) keeps it selected.
  if (!props.single || values?.length) {
    modelValue.value = props.single ? (values ?? []).slice(-1) : values ?? []
  }
  if (props.single) closePanel()
}

function onKeydown(event: KeyboardEvent) {
  if (
    event.target instanceof HTMLElement &&
    event.target.classList.contains('p-multiselect-filter')
  )
    return
  // MultiSelect's select-all shortcut has no meaning for a single selection.
  if (props.single && (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'a') {
    event.preventDefault()
    event.stopPropagation()
  }
  if (
    props.single &&
    event.shiftKey &&
    ['ArrowUp', 'ArrowDown', 'Home', 'End', 'Enter', ' '].includes(event.key)
  ) {
    event.preventDefault()
    event.stopPropagation()
    event.target?.dispatchEvent(
      new KeyboardEvent('keydown', {
        key: event.key,
        code: event.code,
        bubbles: true,
        cancelable: true
      })
    )
  }
}

const multiSelectRef = useTemplateRef<{ hide: () => void }>('multi-select')

function closePanel() {
  multiSelectRef.value?.hide()
}

// Wrap primitive options too: PrimeVue treats a matched option of 0 as missing.
const primeOptions = computed(() =>
  props.options.map((item) => ({
    item,
    label: getOptionLabel(item),
    searchText: props.optionSearchText?.(item) ?? getOptionLabel(item),
    value: getOptionValue(item)
  }))
)

function getOptionLabel(option: TOption) {
  if (typeof props.optionLabel === 'function') return props.optionLabel(option)
  if (typeof props.optionLabel === 'string')
    return String(option[props.optionLabel as keyof TOption])

  return String(option)
}

function getOptionValue(option: TOption) {
  if (typeof props.optionValue === 'function') return props.optionValue(option)
  if (typeof props.optionValue === 'string') return option[props.optionValue as keyof TOption]

  return option
}

function isOptionSelected(option: TOption) {
  const selectedValues = modelValue.value ?? []
  const optionValue = getOptionValue(option)

  return selectedValues.some((selectedValue) => Object.is(selectedValue, optionValue))
}
</script>
